<template>
  <div class="financial-report-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>{{ $t('financialReport.search.title') }}</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="6">
          <el-date-picker
            v-model="searchForm.monthRange"
            type="monthrange"
            :placeholder="$t('financialReport.search.placeholderMonthRange')"
            format="yyyy-MM"
            value-format="yyyy-MM"
            class="w-100"
          />
        </el-col>
        <el-col :span="5">
          <el-select
            v-model="searchForm.feeTypeCds"
            multiple
            collapse-tags
            :placeholder="$t('financialReport.search.placeholderFeeType')"
            class="w-100"
          >
            <el-option
              v-for="item in feeTypeOptions"
              :key="item.statusCd"
              :label="item.name"
              :value="item.statusCd"
            />
          </el-select>
        </el-col>
        <el-col :span="5">
          <el-button type="primary" @click="handleSearch">{{ $t('financialReport.search.search') }}</el-button>
          <el-button @click="handleReset">{{ $t('financialReport.search.reset') }}</el-button>
        </el-col>
      </el-row>
    </el-card>

    <el-card class="box-card mt-20">
      <div slot="header" class="clearfix flex justify-between">
        <span>{{ $t('financialReport.table.title') }}</span>
        <el-button type="success" size="small" @click="handleExport">{{ $t('financialReport.export') }}</el-button>
      </div>
      <el-table :data="tableData" border style="width: 100%" v-loading="loading" show-summary :summary-method="getSummaries">
        <el-table-column prop="communityName" :label="$t('financialReport.table.communityName')" align="center" min-width="140" fixed="left" />
        <el-table-column prop="hisOweFee" :label="$t('financialReport.table.hisOweFee')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.hisOweFee) }}</template>
        </el-table-column>
        <el-table-column prop="curReceivableFee" :label="$t('financialReport.table.curReceivableFee')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.curReceivableFee) }}</template>
        </el-table-column>
        <el-table-column prop="curReceivedFee" :label="$t('financialReport.table.curReceivedFee')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.curReceivedFee) }}</template>
        </el-table-column>
        <el-table-column prop="curOweFee" :label="$t('financialReport.table.curOweFee')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.curOweFee) }}</template>
        </el-table-column>
        <el-table-column prop="totalOweFee" :label="$t('financialReport.table.totalOweFee')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.totalOweFee) }}</template>
        </el-table-column>
        <el-table-column prop="actualPaid" :label="$t('financialReport.table.actualPaid')" align="right" width="130">
          <template slot-scope="scope">{{ formatMoney(scope.row.actualPaid) }}</template>
        </el-table-column>
        <el-table-column prop="feeRoomRate" :label="$t('financialReport.table.feeRoomRate')" align="center" width="110">
          <template slot-scope="scope">
            <span :style="{ color: getRateColor(scope.row.feeRoomRate) }">{{ formatRate(scope.row.feeRoomRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="feeRate" :label="$t('financialReport.table.feeRate')" align="center" width="100">
          <template slot-scope="scope">
            <span :style="{ color: getRateColor(scope.row.feeRate) }">{{ formatRate(scope.row.feeRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="clearRate" :label="$t('financialReport.table.clearRate')" align="center" width="100">
          <template slot-scope="scope">
            <span :style="{ color: getRateColor(scope.row.clearRate) }">{{ formatRate(scope.row.clearRate) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="mt-10 text-right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.row"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pagination.total"
      />
    </el-card>
  </div>
</template>

<script>
import { getFinancialReport } from '@/api/report/financialReportApi'
import { getMyEnteredCommunitys } from '@/api/community/communityApi'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'

export default {
  name: 'FinancialReportList',
  data() {
    return {
      searchForm: {
        monthRange: [],
        feeTypeCds: []
      },
      feeTypeOptions: [],
      tableData: [],
      loading: false,
      pagination: {
        page: 1,
        row: 20,
        total: 0
      },
      communityMap: {}
    }
  },
  created() {
    this.initMonthRange()
    this.loadFeeTypes()
    this.loadCommunities()
    this.fetchData()
  },
  methods: {
    initMonthRange() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      this.searchForm.monthRange = [`${year}-01`, `${year}-${month}`]
    },
    async loadCommunities() {
      try {
        const res = await getMyEnteredCommunitys({ page: 1, row: 500 })
        const list = res.communitys || []
        list.forEach(c => {
          this.$set(this.communityMap, c.communityId, c.name)
        })
      } catch (e) {
        console.error(e)
      }
    },
    loadFeeTypes() {
      this.$store.dispatch('dict/getDict', {
        dictType: 'pay_fee_config',
        dictName: 'fee_type_cd'
      }).then(data => {
        this.feeTypeOptions = data
      })
    },
    buildParams() {
      return {
        startDate: this.searchForm.monthRange ? this.searchForm.monthRange[0] : '',
        endDate: this.searchForm.monthRange ? this.searchForm.monthRange[1] : '',
        feeTypeCds: this.searchForm.feeTypeCds ? this.searchForm.feeTypeCds.join(',') : '',
        page: this.pagination.page,
        row: this.pagination.row
      }
    },
    async fetchData() {
      try {
        this.loading = true
        const params = this.buildParams()
        const res = await getFinancialReport(params)
        if (res.data && res.data.code === 0) {
          this.tableData = res.data.data || []
          this.pagination.total = res.data.total || 0
        }
      } catch (error) {
        this.$message.error(this.$t('financialReport.fetchError'))
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.pagination.page = 1
      this.fetchData()
    },
    handleReset() {
      this.searchForm = { monthRange: [], feeTypeCds: [] }
      this.initMonthRange()
      this.pagination = { page: 1, row: 20, total: 0 }
      this.tableData = []
    },
    handleSizeChange(val) {
      this.pagination.row = val
      this.pagination.page = 1
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.pagination.page = val
      this.fetchData()
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatRate(value) {
      return (Number(value || 0).toFixed(2)) + '%'
    },
    getRateColor(rate) {
      if ((rate || 0) >= 90) return '#67C23A'
      if ((rate || 0) >= 60) return '#E6A23C'
      return '#F56C6C'
    },
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) { sums[index] = '合计'; return }
        if (['feeRoomRate', 'feeRate', 'clearRate'].includes(column.property)) { sums[index] = ''; return }
        const values = data.map(item => Number(item[column.property]))
        sums[index] = values.every(v => !isNaN(v)) ? values.reduce((prev, curr) => prev + curr, 0).toFixed(2) : ''
      })
      return sums
    },
    async handleExport() {
      this.loading = true
      try {
        const params = this.buildParams()
        params.page = 1
        params.row = 99999
        const res = await getFinancialReport(params)
        if (!res.data || res.data.code !== 0 || !res.data.data || res.data.data.length === 0) {
          this.$message.warning('没有数据可导出')
          return
        }
        const headers = ['小区名称', '历史欠费', '当期应收', '当期实收', '当期欠费', '总欠费', '实缴', '户收费率', '缴费率', '清缴率']
        const rows = res.data.data.map(row => [
          row.communityName || this.communityMap[row.communityId] || row.communityId,
          this.formatMoney(row.hisOweFee),
          this.formatMoney(row.curReceivableFee),
          this.formatMoney(row.curReceivedFee),
          this.formatMoney(row.curOweFee),
          this.formatMoney(row.totalOweFee),
          this.formatMoney(row.actualPaid),
          this.formatRate(row.feeRoomRate),
          this.formatRate(row.feeRate),
          this.formatRate(row.clearRate)
        ])
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, '财务报表')
        const blob = new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' })
        saveAs(blob, '财务报表.xlsx')
      } catch (error) {
        this.$message.error('导出失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.financial-report-container {
  padding: 20px;
  .box-card { margin-bottom: 20px; }
  .mt-20 { margin-top: 20px; }
  .mt-10 { margin-top: 10px; }
  .w-100 { width: 100%; }
  .text-right { text-align: right; }
  .flex { display: flex; align-items: center; }
  .justify-between { justify-content: space-between; }
}
</style>
